// src/utils/outOfScopeDetector.js
// Pure utility — no side effects, no Firebase imports.

/**
 * Each rule has:
 *   id      – unique identifier for debugging
 *   test    – (normalizedText: string) => boolean
 *   reason  – human-readable explanation shown to the user
 */
const RULES = [
  {
    id: "account_review_suspension",
    test(text) {
      const hasAccount =
        text.includes("stripe account") ||
        text.includes("my account") ||
        text.includes("our account") ||
        text.includes("account has been");
      const hasSuspension =
        text.includes("under review") ||
        text.includes("suspended") ||
        text.includes("banned") ||
        text.includes("restricted") ||
        text.includes("terminated") ||
        text.includes("deactivated");
      return hasAccount && hasSuspension;
    },
    reason:
      "Stripe account reviews and risk holds are managed by Stripe's internal compliance team. No expert can override, appeal, or accelerate this process. You will need to work directly with Stripe Support to resolve your account status.",
  },

  {
    id: "fraud_risk",
    test(text) {
      return (
        text.includes("fraud") ||
        text.includes("risk hold") ||
        text.includes("risk team") ||
        text.includes("flagged by stripe") ||
        text.includes("stripe risk")
      );
    },
    reason:
      "Fraud flags and risk holds are handled exclusively by Stripe's Risk and Trust & Safety teams. No external specialist can remove, appeal, or negotiate a risk decision on your behalf.",
  },

  {
    id: "kyc_identity",
    test(text) {
      return (
        text.includes("kyc") ||
        text.includes("identity verification") ||
        text.includes("verification required by stripe") ||
        text.includes("stripe verification") ||
        text.includes("verify my identity") ||
        text.includes("verify our identity")
      );
    },
    reason:
      "KYC and identity verification requests are initiated and resolved by Stripe's compliance team. This process cannot be expedited or completed by a third-party technical expert.",
  },

  {
    id: "chargeback_dispute",
    test(text) {
      return (
        text.includes("chargeback") ||
        text.includes("dispute") ||
        text.includes("refund dispute")
      );
    },
    reason:
      "Chargebacks and payment disputes are resolved between your customer, their bank, and Stripe directly. An expert session cannot overturn a dispute outcome or recover disputed funds.",
  },

  {
    id: "ads_account_ban",
    test(text) {
      const hasAdPlatform =
        text.includes("google ads") ||
        text.includes("meta ads") ||
        text.includes("facebook ads");
      const hasBan =
        text.includes("banned") ||
        text.includes("suspended") ||
        text.includes("account disabled") ||
        text.includes("ad account");
      return hasAdPlatform && hasBan;
    },
    reason:
      "Ad account bans require a direct appeal to Google or Meta through their official review process. This is outside the scope of a technical expert session.",
  },

  {
    id: "stripe_support_contact",
    test(text) {
      return (
        text.includes("stripe support") ||
        text.includes("contact stripe") ||
        text.includes("stripe ticket") ||
        text.includes("opened a ticket") ||
        text.includes("submitted a ticket") ||
        text.includes("waiting for stripe")
      );
    },
    reason:
      "It sounds like your issue is already in Stripe's support queue. Our specialists work on technical integrations — if you have a separate technical component to resolve in the meantime, describe that specific part instead.",
  },

  {
    id: "stripe_platform_decision",
    test(text) {
      return (
        text.includes("stripe told me") ||
        text.includes("stripe said") ||
        text.includes("stripe informed me") ||
        text.includes("stripe replied") ||
        text.includes("stripe emailed me")
      );
    },
    reason:
      "It looks like Stripe has already communicated a platform-side decision about your account. Our experts handle technical integrations — they cannot reverse or negotiate internal decisions made by Stripe.",
  },
];

/**
 * Checks submitted form data against all out-of-scope rules.
 *
 * Combines problem description, optional title, and selected tags into a
 * single lowercase string before matching, so all fields are checked at once.
 *
 * @param {{ problem?: string, title?: string, selectedTags?: string[] }} formData
 * @returns {{ isOutOfScope: boolean, reason: string | null }}
 */
export function detectOutOfScope(formData) {
  const combined = [
    formData.problem || "",
    formData.title || "",
    (formData.selectedTags || []).join(" "),
  ]
    .join(" ")
    .toLowerCase()
    .trim();

  for (const rule of RULES) {
    if (rule.test(combined)) {
      return { isOutOfScope: true, reason: rule.reason };
    }
  }

  return { isOutOfScope: false, reason: null };
}
